import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import '@tabler/core/dist/css/tabler.min.css';
import { IconSend, IconFile, IconFileZip, IconFileText, IconLogout, IconPaperclip, IconX, IconSearch, IconChevronUp, IconChevronDown, IconArrowLeft } from '@tabler/icons-react';
import './App.css';
import WhatsAppQRModal from './WhatsAppQRModal';
import { useTheme } from './useTheme';

// Configurar axios para enviar el token en todas las peticiones
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// --- FUNCIÓN AUXILIAR PARA FECHAS ---
const formatDateSeparator = (dateString) => {
  const messageDate = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const options = { year: 'numeric', month: 'long', day: 'numeric' };

  if (messageDate.toDateString() === today.toDateString()) return 'HOY';
  if (messageDate.toDateString() === yesterday.toDateString()) return 'AYER';
  return messageDate.toLocaleDateString('es-MX', options);
};


// --- FUNCIÓN AUXILIAR PARA URLS DE MEDIA ---
const getMediaUrl = (path) => {
  // Si ya es una URL completa, devolverla tal cual
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // En desarrollo, el backend está en puerto 3003
  const isDevelopment = window.location.port === '3000';
  if (isDevelopment) {
    return `http://localhost:3003${path}`;
  }

  // En producción, usar la URL actual del dominio
  return path;
};

// --- COMPONENTE PRINCIPAL DE LA PÁGINA DE CHAT ---
function ChatPage() {
  // --- ESTADOS Y REFS ---
  const { contacto } = useParams();
  const navigate = useNavigate();
  const numeroDestino = contacto ? decodeURIComponent(contacto) : null;
  useTheme(); // Sincronizar tema con localStorage
  const [mensajes, setMensajes] = useState([]);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Estados para búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [whatsappReady, setWhatsappReady] = useState(false);
  const virtuosoRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [atBottom, setAtBottom] = useState(true);


  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  // --- LÓGICA DE BÚSQUEDA ---
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      setCurrentResultIndex(0);
      return;
    }

    const term = searchTerm.toLowerCase();
    const results = mensajes
      .map((mensaje, index) => ({ mensaje, originalIndex: index }))
      .filter(({ mensaje }) => {
        // Solo buscar en mensajes de texto (no en rutas de archivos)
        if (!mensaje.mensaje || typeof mensaje.mensaje !== 'string') return false;
        if (mensaje.mensaje.startsWith('/media/')) return false;
        return mensaje.mensaje.toLowerCase().includes(term);
      });

    setSearchResults(results);
    setCurrentResultIndex(results.length > 0 ? 0 : -1);

    // Scroll al primer resultado
    if (results.length > 0 && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: results[0].originalIndex,
        align: 'center',
        behavior: 'smooth'
      });
    }
  }, [searchTerm, mensajes]);

  const handleNextResult = () => {
    if (searchResults.length === 0) return;
    const newIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(newIndex);
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: searchResults[newIndex].originalIndex,
        align: 'center',
        behavior: 'smooth'
      });
    }
  };

  const handlePrevResult = () => {
    if (searchResults.length === 0) return;
    const newIndex = currentResultIndex === 0 ? searchResults.length - 1 : currentResultIndex - 1;
    setCurrentResultIndex(newIndex);
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: searchResults[newIndex].originalIndex,
        align: 'center',
        behavior: 'smooth'
      });
    }
  };

  const highlightText = (text, highlight) => {
    if (!highlight.trim()) return text;

    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <mark key={index} style={{ backgroundColor: '#ffd43b', padding: '0 2px', borderRadius: '2px' }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // --- LÓGICA DE DATOS Y WEBSOCKETS ---
  const fetchMensajes = useCallback(async () => {
    if (!numeroDestino) return;
    try {
      const url = numeroDestino
        ? `/api/mensajes?contacto=${encodeURIComponent(numeroDestino)}`
        : '/api/mensajes';
      const response = await axios.get(url);
      setMensajes(response.data.reverse());
    } catch (error) {
      console.error('Error al obtener los mensajes:', error);
    }
  }, [numeroDestino]);

  useEffect(() => {
    fetchMensajes(); // Carga inicial del historial

    // En desarrollo, el WebSocket está en el puerto 3003
    const isDevelopment = window.location.port === '3000';
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = isDevelopment ? 'localhost:3003' : window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}`;
    console.log('Conectando WebSocket a:', wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => console.log('Conectado al servidor WebSocket.');
    ws.onclose = () => console.log('Desconectado del servidor WebSocket.');

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Manejar diferentes tipos de mensajes
        if (data.type === 'qr') {
          // Recibido código QR de WhatsApp
          console.log('QR recibido');
          setQrCode(data.qr);
          setWhatsappReady(false);
        } else if (data.type === 'whatsapp-ready') {
          // WhatsApp conectado
          console.log('WhatsApp listo');
          setQrCode(null);
          setWhatsappReady(true);
        } else {
          // Mensaje normal de chat
          setMensajes(prev => [...prev, data]);
        }
      } catch (error) {
        console.error("Error al procesar mensaje de WebSocket:", error);
      }
    };

    return () => ws.close();
  }, [fetchMensajes]);



  // --- MANEJADORES DE ARCHIVOS ---
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño (máx 16MB)
    if (file.size > 16 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 16MB.');
      return;
    }

    setSelectedFile(file);

    // Crear vista previa para imágenes
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleCancelFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendFile = async () => {
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('archivo', selectedFile);
    formData.append('numero', numeroDestino);
    formData.append('caption', nuevoMensaje || '');

    try {
      await axios.post('/api/enviar-archivo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      // Limpiar
      setSelectedFile(null);
      setFilePreview(null);
      setNuevoMensaje('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error al enviar el archivo:', error);
      alert('Hubo un error al enviar el archivo.');
    } finally {
      setUploading(false);
    }
  };

  // --- MANEJADOR DE ENVÍO DE MENSAJES (CORREGIDO) ---
  const handleEnviarMensaje = async (e) => {
    e.preventDefault();

    // Si hay un archivo seleccionado, enviarlo
    if (selectedFile) {
      await handleSendFile();
      return;
    }

    if (!nuevoMensaje.trim()) return;
    try {
      // Solo enviamos el mensaje. La actualización la hará el WebSocket.
      await axios.post('/api/enviar-mensaje', { numero: numeroDestino, mensaje: nuevoMensaje });
      setNuevoMensaje('');
    } catch (error) {
      console.error('Error al enviar el mensaje:', error);
      alert('Hubo un error al enviar el mensaje.');
    }
  };

  // --- FUNCIÓN PARA RENDERIZAR CADA FILA ---
  const renderRow = useCallback((index, mensaje) => {
    const prevMensaje = index > 0 ? mensajes[index - 1] : null;
    const showDateSeparator = !prevMensaje || new Date(mensaje.timestamp).toDateString() !== new Date(prevMensaje.timestamp).toDateString();

    // Verificar si este mensaje es un resultado de búsqueda actual
    const isCurrentSearchResult = searchResults.length > 0 &&
                                   searchResults[currentResultIndex]?.originalIndex === index;

    return (
      <div className="chat-row-container" style={{ paddingTop: index === 0 ? '1rem' : 0 }}>
        {showDateSeparator && (
          <div className="chat-date-separator">
            <span>{formatDateSeparator(mensaje.timestamp)}</span>
          </div>
        )}
        <div className={`chat-item ${mensaje.remitente === 'yo' ? 'sent' : 'received'} ${isCurrentSearchResult ? 'search-highlight' : ''}`}>
          <div className="chat-bubble">
            <div className="chat-bubble-text">
              {mensaje.mensaje && typeof mensaje.mensaje === 'string' && mensaje.mensaje.startsWith('/media/') ? (
                (() => {
                  const path = mensaje.mensaje;
                  const filename = path.split('/').pop();

                  if (path.match(/\.(jpeg|jpg|gif|png|webp)$/i)) {
                    return <img src={getMediaUrl(path)} alt={filename} className="chat-image-preview" />;
                  }
                  // if (path.match(/\.(mp3|ogg|wav)$/i)) {
                  //   return (
                  //     <div className="chat-file-preview">
                  //       <IconFileMusic size={32} />
                  //       <div className="file-info">
                  //         <span>{filename}</span>
                  //         <a href={getMediaUrl(path)} target="_blank" rel="noopener noreferrer">Escuchar</a>
                  //       </div>
                  //     </div>
                  //   );
                  // }
                  if (path.match(/\.(ogg|oga|mp3|wav)$/i)) {
                    return (
                      <div className="chat-audio-player">
                        <audio src={getMediaUrl(path)} controls controlsList="nodownload" />
                        <div className="audio-info">
                          <span className="text-muted small">Audio</span>
                        </div>
                      </div>
                    );
                  }
                  let fileIcon = <IconFile size={32} />;
                  if (path.match(/\.(zip|rar)$/i)) fileIcon = <IconFileZip size={32} />;
                  if (path.match(/\.(txt|pdf|doc|docx)$/i)) fileIcon = <IconFileText size={32} />;

                  return (
                    <div className="chat-file-preview">
                      {fileIcon}
                      <div className="file-info">
                        <span>{filename}</span>
                        <a href={getMediaUrl(path)} target="_blank" rel="noopener noreferrer">Descargar</a>
                      </div>
                    </div>
                  );
                })()
              ) : (
                searchTerm ? highlightText(mensaje.mensaje, searchTerm) : mensaje.mensaje
              )}
            </div>
            <div className="chat-bubble-meta">
              {new Date(mensaje.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      </div>
    );
  }, [mensajes, searchTerm, searchResults, currentResultIndex]);

  // Función para formatear número de teléfono
  const formatPhoneNumber = (numero) => {
    if (!numero) return '';
    const cleanNumber = numero.replace('@c.us', '');
    if (cleanNumber.length >= 10) {
      const codigo = cleanNumber.slice(0, 2);
      const uno = cleanNumber.slice(2, 3);
      const area = cleanNumber.slice(3, 5);
      const num1 = cleanNumber.slice(5, 9);
      const num2 = cleanNumber.slice(9);
      return `+${codigo} ${uno} ${area} ${num1} ${num2}`;
    }
    return cleanNumber;
  };

  // --- RENDERIZADO DEL COMPONENTE ---
  if (!numeroDestino) {
    return (
      <div className="page page-center">
        <div className="container-tight py-4">
          <div className="card">
            <div className="card-body text-center">
              <p>No se ha seleccionado ningún contacto</p>
              <button className="btn btn-primary" onClick={() => navigate('/conversations')}>
                Ver conversaciones
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page-center">
      <div className="container-tight py-4">
        <div className="card card-chat">
          <div className="card-header">
            <div className="d-flex align-items-center gap-2">
              {contacto && (
                <button
                  className="btn btn-ghost-secondary btn-icon"
                  onClick={() => navigate('/conversations')}
                  title="Volver a conversaciones"
                >
                  <IconArrowLeft size={20} />
                </button>
              )}
              <h3 className="card-title mb-0">Chat con {formatPhoneNumber(numeroDestino)}</h3>
            </div>
            <div className="ms-auto d-flex gap-2 align-items-center">
              {showSearchBar && (
                <div className="d-flex align-items-center gap-2 me-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Buscar en la conversación..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: '250px' }}
                    autoFocus
                  />
                  {searchResults.length > 0 && (
                    <div className="d-flex align-items-center gap-1">
                      <span className="text-muted small">
                        {currentResultIndex + 1} de {searchResults.length}
                      </span>
                      <button
                        className="btn btn-sm btn-icon"
                        onClick={handlePrevResult}
                        title="Anterior"
                      >
                        <IconChevronUp size={18} />
                      </button>
                      <button
                        className="btn btn-sm btn-icon"
                        onClick={handleNextResult}
                        title="Siguiente"
                      >
                        <IconChevronDown size={18} />
                      </button>
                    </div>
                  )}
                  <button
                    className="btn btn-sm btn-icon"
                    onClick={() => {
                      setShowSearchBar(false);
                      setSearchTerm('');
                    }}
                    title="Cerrar búsqueda"
                  >
                    <IconX size={18} />
                  </button>
                </div>
              )}
              {!showSearchBar && (
                <button
                  className="btn btn-icon"
                  onClick={() => setShowSearchBar(true)}
                  title="Buscar"
                >
                  <IconSearch />
                </button>
              )}
              <button className="btn btn-icon" onClick={handleLogout} title="Cerrar sesión">
                <IconLogout />
              </button>
            </div>
          </div>
          <Virtuoso
            ref={virtuosoRef}
            className="card-body-scrollable"
            data={mensajes}
            followOutput="auto"
            initialTopMostItemIndex={mensajes.length - 1} // <-- ESTA ES LA LÍNEA CLAVE
            itemContent={renderRow}
          />
          <div className="card-footer">
            {/* Vista previa del archivo seleccionado */}
            {selectedFile && (
              <div className="file-preview-container mb-2 p-2 bg-light rounded">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    {filePreview ? (
                      <img src={filePreview} alt="Preview" style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }} />
                    ) : (
                      <IconFile size={50} />
                    )}
                    <div>
                      <div className="fw-bold">{selectedFile.name}</div>
                      <small className="text-muted">{(selectedFile.size / 1024).toFixed(2)} KB</small>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost-danger"
                    onClick={handleCancelFile}
                    title="Cancelar"
                  >
                    <IconX size={18} />
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleEnviarMensaje}>
              <div className="input-group">
                {/* Input oculto para archivos */}
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                />

                {/* Botón adjuntar */}
                <button
                  type="button"
                  className="btn btn-icon"
                  onClick={() => fileInputRef.current?.click()}
                  title="Adjuntar archivo"
                  disabled={uploading}
                >
                  <IconPaperclip size={20} />
                </button>

                <input
                  type="text"
                  className="form-control"
                  placeholder={selectedFile ? "Añade un comentario (opcional)..." : "Escribe un mensaje..."}
                  value={nuevoMensaje}
                  onChange={(e) => setNuevoMensaje(e.target.value)}
                  disabled={uploading}
                />
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={uploading || (!nuevoMensaje.trim() && !selectedFile)}
                >
                  {uploading ? '...' : <IconSend size={18} />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modal de QR de WhatsApp */}
      <WhatsAppQRModal
        qrCode={qrCode}
        onClose={() => setQrCode(null)}
      />
    </div>
  );
}

export default ChatPage;